/**
 * GMA-1624 (CompassCare Web 9/14 — Nora SitRep engine) — pure engine invariants.
 *
 * Nora generates the SITRep prose (AI, backend). This module owns the pure,
 * deterministic rules the UI relies on: cadence → data window, per-observation
 * confidence labeling (a device-offline window forces LOW — AC), overall
 * GREEN/AMBER/RED status, and the family-output jargon guard (AC: no BLUF/ADL
 * jargon in family renderings).
 *
 * All functions fail safe: bad input yields a benign result, never a throw.
 */

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

export const CADENCES = Object.freeze(["daily", "weekly", "monthly"]);

const CADENCE_WINDOW_MS = Object.freeze({
  daily: DAY_MS,
  weekly: 7 * DAY_MS,
  monthly: 30 * DAY_MS,
});

/** Data window (ms) for a cadence; unknown/missing → daily. */
export function windowMsForCadence(cadence) {
  return Object.prototype.hasOwnProperty.call(CADENCE_WINDOW_MS, cadence)
    ? CADENCE_WINDOW_MS[cadence]
    : CADENCE_WINDOW_MS.daily;
}

export const CONFIDENCE_LEVELS = Object.freeze(["high", "moderate", "low"]);

const HIGH_SOURCES = Object.freeze(["telemetry", "logs", "direct"]);
const MODERATE_SOURCES = Object.freeze(["inference", "pattern"]);

/**
 * Confidence label for an observation. A device-offline window forces LOW
 * regardless of source (AC). Direct telemetry/logs → HIGH; pattern inference →
 * MODERATE; partial/interrupted/unknown → LOW (conservative).
 * @param {{source?:string, deviceOffline?:boolean}} observation
 * @returns {"high"|"moderate"|"low"}
 */
export function resolveConfidence(observation) {
  if (!observation || typeof observation !== "object") return "low";
  if (observation.deviceOffline === true) return "low";
  const src = observation.source;
  if (HIGH_SOURCES.includes(src)) return "high";
  if (MODERATE_SOURCES.includes(src)) return "moderate";
  return "low";
}

export const OVERALL_STATUSES = Object.freeze(["green", "amber", "red"]);

/**
 * Overall masthead status from the developments: RED if any is critical, AMBER
 * if any is watch, else GREEN.
 * @param {Array<{priority?:string}>} developments
 * @returns {"green"|"amber"|"red"}
 */
export function overallStatus(developments) {
  const list = Array.isArray(developments) ? developments : [];
  let amber = false;
  for (const d of list) {
    const p = d && d.priority;
    if (p === "critical") return "red";
    if (p === "watch") amber = true;
  }
  return amber ? "amber" : "green";
}

// Clinician-only jargon that must never appear in family/caregiver output.
// Matched as whole words (case-insensitive) so normal words that merely contain
// these letters (e.g. "bluffing" contains "bluf", "cradle" contains "adl") are
// NOT flagged.
const CLINICIAN_JARGON = Object.freeze([
  "bluf",
  "adl",
  "adls",
  "prn",
  "sitrep",
  "baseline delta",
]);

// Precompile one whole-word, case-insensitive matcher per term (hasClinicianJargon
// is called on the render path, so we don't want to rebuild these each call).
const JARGON_PATTERNS = Object.freeze(
  CLINICIAN_JARGON.map(
    (term) =>
      // Escape regex metachars, then let any whitespace run join multi-word
      // terms ("baseline delta" matches across double spaces/newlines/tabs —
      // Copilot #309, same normalization other text guards here use).
      // "iu" + /\s+/gu to match the text-guard convention in
      // alertsQueueModel.js (Copilot #309).
      new RegExp(
        `\\b${term
          .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
          .replace(/\s+/gu, "\\s+")}\\b`,
        "iu",
      ),
  ),
);

/**
 * Whether family-facing text contains clinician jargon (AC string check).
 * @param {string} text
 * @returns {boolean}
 */
export function hasClinicianJargon(text) {
  if (typeof text !== "string") return false;
  return JARGON_PATTERNS.some((re) => re.test(text));
}
